// A simpler approach to URL-synchronized filters

import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
/**
 * Hook to manage filters that are synchronized with URL search parameters
 * @param filterKeys An array of filter keys to track in the URL
 * @returns Tools for managing the filters
 */
export function useUrlFilters<
  T extends {
    keyStagesSlug?: string[];
    threadSlugs?: string[];
  }
>({ filterKeys, resources }: { filterKeys?: string[]; resources?: T[] }) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current values from URL
  const filterOptions = useMemo(() => {
    const values: Record<string, string[]> = {};

    filterKeys?.forEach((key) => {
      const param = searchParams.get(key);
      values[key] = param ? param.split(",").filter(Boolean) : [];
    });

    return values;
  }, [filterKeys, searchParams]);

  // Set a filter value and update URL
  const setFilterValue = useCallback(
    (key: string, values: string[]) => {
      const params = new URLSearchParams(searchParams);

      if (values.length > 0) {
        params.set(key, values.join(","));
      } else {
        params.delete(key);
      }

      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Efficient filtering using Sets
  const filteredResources = useMemo(() => {
    // If no filters, return all resources
    if (!filterOptions.keyStages?.length && !filterOptions.threads?.length) {
      return resources;
    }

    const keyStageSet = filterOptions.keyStages
      ? new Set(filterOptions.keyStages)
      : null;
    const threadSet = filterOptions.threads
      ? new Set(filterOptions.threads)
      : null;

    return resources?.filter((resource) => {
      let keyStageMatch = true;
      let threadMatch = true;

      if (keyStageSet && keyStageSet.size > 0) {
        keyStageMatch =
          Array.isArray(resource.keyStagesSlug) &&
          resource.keyStagesSlug.some((ks) => keyStageSet.has(ks));
      }
      if (threadSet && threadSet.size > 0) {
        threadMatch =
          Array.isArray(resource.threadSlugs) &&
          resource.threadSlugs.some((th) => threadSet.has(th));
      }
      return keyStageMatch && threadMatch;
    });
  }, [resources, filterOptions]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    const params = new URLSearchParams();
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  return {
    filterOptions,
    filteredResources,
    setFilterValue,
    clearAllFilters,
  };
}
